/*
 * Copyright © 2020-2025 XCamera. All Rights Reserved.
 * @Description: 通用选择器模态框组件
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-08-08 20:06:12
 */
import React, { useState, useCallback, ReactNode, useEffect } from 'react'
import {
  Modal,
  Button,
  List,
  Row,
  Col,
  Card,
  Checkbox,
  Typography,
  Input
} from 'antd'
import './index.scss'

/**
 * 通用选择器数据项接口
 * 要求必须包含id字段
 */
export interface XCGenericSelectorItem {
  id: string
  /**
   * 可选的选中状态字段
   * 当值为1时，该项目将被默认选中
   */
  selected?: boolean
  [key: string]: any
}

/**
 * 通用选择器模态框属性接口
 */
export interface GenericSelectorModalProps<T extends XCGenericSelectorItem> {
  /**
   * 模态框显示状态
   */
  visible: boolean
  /**
   * 模态框标题
   */
  title: string
  /**
   * 数据源数组
   */
  dataSource: T[]
  /**
   * 选择类型：单选或多选
   */
  selectionType?: 'single' | 'multiple'
  /**
   * 每行显示的列数
   */
  columnsPerRow?: 1 | 2 | 3 | 4 | 6 | 8 | 12 | 24
  /**
   * 自定义渲染选项的函数（可选）
   * 如果不提供，将使用默认的卡片样式
   */
  renderItem?: (
    item: T,
    isSelected: boolean,
    onSelect: (itemId: string) => void
  ) => ReactNode
  /**
   * 确认回调函数
   */
  onConfirm?: (
    selectedItems: T[],
    rememberChoice: boolean,
    dataSource: T[]
  ) => void
  /**
   * 取消回调函数
   */
  onCancel?: () => void
  /**
   * 确认按钮文本
   */
  confirmText?: string
  /**
   * 确认按钮是否禁用（当没有选择项时）
   */
  disableConfirmWhenEmpty?: boolean
  /**
   * 模态框宽度
   */
  width?: number
  /**
   * 是否启用自定义内容输入
   */
  enableCustomInput?: boolean
  /**
   * 自定义选项的显示文本
   */
  customOptionText?: string
  /**
   * 自定义输入框的提醒文案
   */
  customInputPlaceholder?: string
  /**
   * 自定义内容变化回调
   */
  onCustomContentChange?: (content: string) => void
  /**
   * 是否显示右上角的关闭按钮
   */
  closable?: boolean
  /**
   * 点击蒙层是否允许关闭
   */
  maskClosable?: boolean
  /**
   * 是否显示"记住本次选择"复选框
   */
  enableRememberChoice?: boolean
  /**
   * "记住本次选择"的默认状态
   */
  defaultRememberChoice?: boolean
  /**
   * "记住本次选择"的自定义文本
   */
  rememberChoiceText?: string
  /**
   * "记住本次选择"状态变化回调
   */
  onRememberChoiceChange?: (selectedItems: T[], rememberChoice: boolean) => void
}

/**
 * 通用选择器模态框组件
 * 支持自定义标题、列数布局、子选项组件和通用数据源
 */
/**
 * 默认渲染选项的函数
 * 使用安全隐患场景选项的样式
 */
function defaultRenderItem<T extends XCGenericSelectorItem>(
  item: T,
  isSelected: boolean,
  onSelect: (itemId: string) => void
): ReactNode {
  // 获取显示名称和描述，支持通用数据结构
  const displayName = (item as any).name || (item as any).title || item.id
  const displayDescription =
    (item as any).description || (item as any).desc || ''

  return (
    <Card
      className={`xc-selector-card ${
        isSelected ? 'xc-selector-card-selected' : ''
      }`}
      onClick={() => onSelect(item.id)}
      hoverable={false}
    >
      <div className="xc-selector-card-content">
        <div className="xc-selector-card-check">
          <Checkbox checked={isSelected} />
        </div>
        <Typography.Title
          level={4}
          style={{
            marginBottom: 6,
            fontFamily: 'PingFang SC',
            fontWeight: 600,
            fontSize: '17px',
            lineHeight: '100%',
            letterSpacing: '0px',
            color: isSelected ? '#0070F4' : '#444444'
          }}
        >
          {displayName}
        </Typography.Title>
        {displayDescription && (
          <Typography.Paragraph
            ellipsis={{ rows: 2 }}
            style={{
              marginTop: 10,
              fontFamily: 'PingFang SC',
              fontWeight: 400,
              fontSize: '13px',
              lineHeight: '20px',
              color: isSelected ? '#0070F4' : '#666666'
            }}
          >
            {displayDescription}
          </Typography.Paragraph>
        )}
      </div>
    </Card>
  )
}

function GenericSelectorModal<T extends XCGenericSelectorItem>({
  visible,
  title,
  dataSource,
  selectionType = 'single',
  columnsPerRow = 2,
  renderItem,
  onConfirm,
  onCancel,
  confirmText = '确认',
  disableConfirmWhenEmpty = true,
  width = 614,
  enableCustomInput = false,
  customOptionText = '自定义',
  customInputPlaceholder = '请输入自定义内容',
  onCustomContentChange,
  closable = true,
  maskClosable = true,
  enableRememberChoice = false,
  defaultRememberChoice = false,
  rememberChoiceText = '记住我的选择',
  onRememberChoiceChange
}: GenericSelectorModalProps<T>) {
  const [selectedItems, setSelectedItems] = useState<T[]>([])
  const [isCustomSelected, setIsCustomSelected] = useState(false)
  const [customContent, setCustomContent] = useState('')
  const [rememberChoice, setRememberChoice] = useState(defaultRememberChoice)

  /**
   * 处理数据源变化时的默认选中状态
   * 如果数组源中有selected字段且值为1，则默认勾选
   */
  useEffect(() => {
    if (dataSource && dataSource.length > 0) {
      const defaultSelectedItems = dataSource.filter(
        (item) => item.selected === true
      )

      if (defaultSelectedItems.length > 0) {
        if (selectionType === 'single') {
          // 单选模式：只选择第一个selected=1的项目
          setSelectedItems([defaultSelectedItems[0]])
        } else {
          // 多选模式：选择所有selected=1的项目
          setSelectedItems(defaultSelectedItems)
        }
      }
    }
  }, [dataSource, selectionType])

  /**
   * 处理组件打开时重置记住选择状态
   * 当组件打开时，重置记住我的选择为不选中
   */
  useEffect(() => {
    if (visible) {
      setRememberChoice(false)
    }
  }, [visible])

  /**
   * 处理选项选择
   */
  const handleItemSelect = useCallback(
    (itemId: string) => {
      setSelectedItems((prev) => {
        const item = dataSource.find((item) => item.id === itemId)
        if (!item) return prev

        const isSelected = prev.some(
          (selectedItem) => selectedItem.id === itemId
        )

        let newSelectedItems: T[]
        if (selectionType === 'single') {
          // 单选模式
          if (isSelected) {
            // 取消选中
            newSelectedItems = []
          } else {
            // 选中当前项，清除其他选项，并取消自定义选项
            setIsCustomSelected(false)
            newSelectedItems = [item]
          }
        } else {
          // 多选模式
          if (isSelected) {
            // 取消选中
            newSelectedItems = prev.filter(
              (selectedItem) => selectedItem.id !== itemId
            )
          } else {
            // 添加选中
            newSelectedItems = [...prev, item]
          }
        }

        // 触发选项变化回调
        if (onRememberChoiceChange) {
          onRememberChoiceChange(newSelectedItems, rememberChoice)
        }

        return newSelectedItems
      })
    },
    [dataSource, selectionType, onRememberChoiceChange, rememberChoice]
  )

  /**
   * 处理自定义选项选择
   */
  const handleCustomSelect = useCallback(() => {
    const newIsCustomSelected = !isCustomSelected
    setIsCustomSelected(newIsCustomSelected)

    if (selectionType === 'single') {
      // 单选模式：选中自定义选项时清除其他选项
      setSelectedItems([])
      // 触发选项变化回调
      if (onRememberChoiceChange) {
        onRememberChoiceChange([], rememberChoice)
      }
    } else {
      // 多选模式：触发选项变化回调
      if (onRememberChoiceChange) {
        onRememberChoiceChange(selectedItems, rememberChoice)
      }
    }
  }, [
    isCustomSelected,
    selectionType,
    onRememberChoiceChange,
    selectedItems,
    rememberChoice
  ])

  /**
   * 处理自定义内容变化
   */
  const handleCustomContentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value
      setCustomContent(value)
      if (onCustomContentChange) {
        onCustomContentChange(value)
      }
    },
    [onCustomContentChange]
  )

  /**
   * 处理记住选择状态变化
   */
  const handleRememberChoiceChange = useCallback(
    (e: any) => {
      const checked = e.target.checked
      setRememberChoice(checked)
      // 触发选项变化回调
      if (onRememberChoiceChange) {
        onRememberChoiceChange(selectedItems, checked)
      }
    },
    [onRememberChoiceChange, selectedItems]
  )

  /**
   * 处理确认按钮点击
   */
  const handleConfirm = useCallback(() => {
    if (onConfirm) {
      let finalSelectedItems = [...selectedItems]

      // 如果选中了自定义选项，添加自定义内容到结果中
      if (isCustomSelected && enableCustomInput) {
        const customItem = ({
          id: '__custom__',
          customContent: customContent,
          [customOptionText]: true
        } as unknown) as T
        finalSelectedItems.push(customItem)
      }

      onConfirm(finalSelectedItems, rememberChoice, dataSource)
    }

    // 最终确认时也触发选项变化回调
    if (onRememberChoiceChange) {
      let finalSelectedItems = [...selectedItems]
      if (isCustomSelected && enableCustomInput) {
        const customItem = ({
          id: '__custom__',
          customContent: customContent,
          [customOptionText]: true
        } as unknown) as T
        finalSelectedItems.push(customItem)
      }
      onRememberChoiceChange(finalSelectedItems, rememberChoice)
    }
  }, [
    selectedItems,
    onConfirm,
    isCustomSelected,
    enableCustomInput,
    customContent,
    customOptionText,
    onRememberChoiceChange,
    rememberChoice
  ])

  /**
   * 处理取消按钮点击
   */
  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel()
    }
  }, [onCancel])

  /**
   * 计算每列的宽度
   */
  const getColumnSpan = () => {
    return Math.floor(24 / columnsPerRow)
  }

  return (
    <Modal
      open={visible}
      closable={closable}
      mask={{ closable: maskClosable }}
      onCancel={handleCancel}
      footer={null}
      width={width}
      className="xc-selector-modal"
    >
      <div className="xc-selector-header">
        <h2 className="xc-selector-title">{title}</h2>
      </div>

      <div className="xc-selector-list">
        <List
          dataSource={
            enableCustomInput
              ? [
                  ...dataSource,
                  { id: '__custom__', [customOptionText]: true } as T
                ]
              : dataSource
          }
          split={false}
          grid={{ gutter: 16, column: columnsPerRow ?? 1 }}
          renderItem={(item) => {
            // 检查是否为自定义选项
            if (item.id === '__custom__') {
              return (
                <List.Item>
                  <Card
                    className={`xc-selector-card ${
                      isCustomSelected ? 'xc-selector-card-selected' : ''
                    }`}
                    onClick={handleCustomSelect}
                    hoverable={false}
                  >
                    <div className="xc-selector-card-content">
                      <div className="xc-selector-card-check">
                        <Checkbox checked={isCustomSelected} />
                      </div>
                      <Typography.Title
                        level={4}
                        style={{
                          marginBottom: 6,
                          fontFamily: 'PingFang SC',
                          fontWeight: 600,
                          fontSize: '17px',
                          lineHeight: '100%',
                          letterSpacing: '0px',
                          color: isCustomSelected ? '#0070F4' : '#444444'
                        }}
                      >
                        {customOptionText}
                      </Typography.Title>
                      <Typography.Paragraph
                        ellipsis={{ rows: 2 }}
                        style={{
                          marginTop: 10,
                          fontFamily: 'PingFang SC',
                          fontWeight: 400,
                          fontSize: '13px',
                          lineHeight: '20px',
                          color: isCustomSelected ? '#0070F4' : '#666666'
                        }}
                      >
                        {`选择后必须输入自定义内容，自定义内容长度不能超过20个字符`}
                      </Typography.Paragraph>
                    </div>
                  </Card>
                </List.Item>
              )
            }

            const isSelected = selectedItems.some(
              (selectedItem) => selectedItem.id === item.id
            )
            return (
              <List.Item>
                {renderItem
                  ? renderItem(item, isSelected, handleItemSelect)
                  : defaultRenderItem(item, isSelected, handleItemSelect)}
              </List.Item>
            )
          }}
        />
      </div>

      {/* 自定义输入框 */}
      {enableCustomInput && isCustomSelected && (
        <div
          className="xc-selector-custom-input"
          style={{ marginTop: 16, marginBottom: 16 }}
        >
          <Input.TextArea
            placeholder={customInputPlaceholder}
            value={customContent}
            onChange={handleCustomContentChange}
            rows={3}
            style={{ width: '100%', fontSize: 16, color: '#444444' }}
          />
        </div>
      )}

      {/* 记住本次选择复选框 */}
      {enableRememberChoice && (
        <div
          className="xc-selector-remember-choice"
          style={{ marginTop: 16, marginBottom: 16 }}
        >
          <Checkbox
            checked={rememberChoice}
            onChange={handleRememberChoiceChange}
            style={{
              fontFamily: 'PingFang SC',
              fontSize: '14px',
              color: '#666666'
            }}
          >
            {rememberChoiceText}
          </Checkbox>
        </div>
      )}

      <Button
        type="primary"
        size="large"
        className="xc-selector-confirm-btn"
        onClick={handleConfirm}
        disabled={
          (disableConfirmWhenEmpty &&
            selectedItems.length === 0 &&
            !isCustomSelected) ||
          (isCustomSelected && customContent.trim().length === 0)
        }
      >
        {confirmText}
      </Button>
    </Modal>
  )
}

export default GenericSelectorModal
